import React from 'react';
import { Search, BarChart3, DollarSign, Target, Building, TrendingUp, CheckCircle, FileText, Shield, Users, HelpCircle } from 'lucide-react';

// Icon mapping for string names to components
const iconMap = {
  'BarChart3': BarChart3,
  'DollarSign': DollarSign,
  'Target': Target,
  'Building': Building,
  'TrendingUp': TrendingUp,
  'CheckCircle': CheckCircle,
  'FileText': FileText,
  'Shield': Shield,
  'Users': Users,
  'Search': Search,
  'HelpCircle': HelpCircle
};

const SearchBar = ({ 
  searchTerm, 
  searchResults, 
  isSearching, 
  handleSearch, 
  handleSearchResultClick, 
  onSectionChange 
}) => {
  
  // Helper function to safely get icon component
  const getIconComponent = (icon) => {
    if (!icon) return HelpCircle; // Fallback icon
    
    // If it's already a React component
    if (typeof icon === 'function' || React.isValidElement(icon)) {
      return icon;
    }
    
    // If it's a string, look it up in the map
    if (typeof icon === 'string') {
      return iconMap[icon] || HelpCircle;
    }
    
    // If it's an object with a name property
    if (typeof icon === 'object' && icon.name) {
      return iconMap[icon.name] || HelpCircle;
    }
    
    // Final fallback
    return HelpCircle;
  };

  // Helper function to get result type styling
  const getTypeStyles = (type) => {
    const styles = {
      company: 'bg-blue-100 text-blue-800',
      section: 'bg-green-100 text-green-800', 
      pipeline: 'bg-orange-100 text-orange-800',
      document: 'bg-purple-100 text-purple-800',
      action: 'bg-red-100 text-red-800',
      default: 'bg-gray-100 text-gray-800'
    };
    return styles[type] || styles.default;
  };

  return (
    <div className="relative search-container">
      <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
      <input
        type="text"
        placeholder="Search materials..."
        value={searchTerm || ''}
        onChange={handleSearch}
        className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
      />
      
      {isSearching && searchTerm?.trim() && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto hide-scrollbar">
          {searchResults && searchResults.length > 0 ? (
            searchResults.map((result, index) => {
              const IconComponent = getIconComponent(result?.icon);
              
              return (
                <div
                  key={`${result?.id || 'result'}-${index}`}
                  onClick={() => handleSearchResultClick && handleSearchResultClick(result, onSectionChange)}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {result?.title || 'Untitled'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {result?.subtitle || 'No description'}
                    </div>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${getTypeStyles(result?.type)}`}>
                    {result?.type || 'item'}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-4 text-center text-sm text-gray-500">
              No results found for "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;